import { extend } from "dayjs";

/**
 * How an asset's value is arrived at. Mirrors the backend enum and the
 * FamilyJewels spreadsheet's "Val type" column.
 *
 * This is a provenance classifier, not an asset category: cash, a life policy
 * and a shotgun are all `attested` because a third party states the number.
 */
/** Statutory exempt-property categories. Mirrors the backend enum. */
export enum EptlCategory {
  HOUSEHOLD = 'household',
  BOOKS_MEDIA = 'books_media',
  FARM = 'farm',
  VEHICLE = 'vehicle',
  MONEY = 'money',
  CHILD_SETOFF = 'child_setoff',
}

export enum ValuationBasis {
  MARKET_QUOTED = 'market_quoted',
  ATTESTED = 'attested',
  APPRAISED = 'appraised',
  STATEMENT = 'statement',
}

/**
 * Where an asset stands in the administration. Mirrors the backend enum.
 *
 * `distributed` and `closed` both take the asset out of the remaining estate
 * value while leaving it on file for the court accounting.
 */
export enum AssetStatus {
  OPEN = 'open',
  DISTRIBUTED = 'distributed',
  CLOSED = 'closed',
}

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  [AssetStatus.OPEN]: 'Open',
  [AssetStatus.DISTRIBUTED]: 'Distributed',
  [AssetStatus.CLOSED]: 'Closed',
};

export interface CreateAssetDto {
  estateIdentifier: string;
  ref: string;
  itemNumber: string;
  marketValue: number;
  estimatedValue: number;
  realizedValue: number;
  internalCashlessDistributionAmount?: number;
  estateValueIfInWill?: number;
  estateValueIfJointlyOwned?: number;
  cashReceived?: number;
  percentageAwayFromEstValue?: number;
  percentJointlyOwned?: number;
  portfolioId: number;
  internalDistributionAmount?: number;
  liabilityBalance: number;
  
  // Existing Fields
  name: string;
  category: string; // 'B', 'R', 'F', 'A'
  location: string;
  locationDetail?: string;
  description: string;
  secondaryIdentifier?: string;
  coveredInWill?: boolean;
  jointlyOwned?: boolean;
  EPTLExemption?: boolean;
  isSmallEstate?: boolean;
  propertyType: string; // 'Equity', 'RealEstate'
  equity: string;
  ticker?: string;
  originalQuantityOwned: number;
  priorDayClosingPrice?: number;
  currentQuantityOwned: number;
  dateOfPurchase: string; // ISO Date String
  costBasis: number;
  lease?: boolean;
  liens: number;
  statementValue?: number;
  dateOfDeathValue?: number;
  distributionValue?: number;
  baselineDate?: string;
  distributionDate?: string;
  baselineSource?: string;
  changeSinceDeath?: number;
  changeSinceDeathPercent?: number;
  changeOnDistribution?: number;
  changeOnDistributionPercent?: number;

  propertyClass?: string;
  jurisdictionState?: string;
  firearmMake?: string;
  firearmModel?: string;
  firearmCaliber?: string;
  firearmSerialNumber?: string;

  netOfLien?: number;
  valueType: string;
  valueSource: string;
  valuationBasis?: ValuationBasis;
  originalMarketValue?: number;
  eptlCategory?: EptlCategory | null;
  status: AssetStatus;
  discountToMarket: number;
  familyDiscount: number;
  internalCashlessDistribution?: boolean;
  internalCashDistribution?: boolean;
  internalCashDistributionAmount?: number;
  sold?: boolean;
  donate?: boolean;
}


export interface PostLiability {
  asset: string; // Reference to the associated asset
  assetName?: string; // Optional: To display asset name in liabilities list/details
  description: string;
  amount: string;
  source: string;
  name: string;
  // Add other liability-related fields as necessary
}

  export interface Liability {
    unsecuredAmount?: number;
    creditorUid?: string;
    collateralDescription?: string;
    lienNature?: string;
    accountReference?: string;
    debtor?: string;
    deathTreatment?: string;
    claimCategory?: string;
    status?: string;
    dateIncurred?: string;
    closedDate?: string;
    dateOfDeathBalance?: number;
    outstandingBalance?: number;
    securedAmount?: number;
    collateralValue?: number;

    uid: string; // or number, ensure consistency
    /**
     * Short reference shown to users, unique within the estate: L-003.
     * Optional because the form builds a draft before the server assigns one.
     */
    code?: string;
    estateId: string;
    asset: string; // Reference to the associated asset
    assetName?: string; // Optional: To display asset name in liabilities list/details
    description: string;
    amount: string;
    source: string;
    name: string;
    createdAt?: Date;
    // Serialised over JSON, so this arrives as an ISO string.
    updatedAt?: string;
    // Add other liability-related fields as necessary
  }
  export interface LiabilitiesResponse {
    data: Liability[];
    total: number;
    page: number;
    pageSize: number;
    // Add other response fields as necessary
  }
  
  export interface NotificationItem {
    id: number;
    uid: string;
    title: string;
    description: string;
    type: 'info' | 'warning' | 'error' | 'success';
    isRead: boolean;
    createdAt: string;
  }
  
  export interface PaginatedNotifications {
    data: NotificationItem[];
    total: number;
    page: number;
    lastPage: number;
  }
export type RecurrenceUnit = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CashFlow {
  purpose?: string;
  id: string;
  estateId: string;
  name: string;
  description: string;
  type: 'Inbound' | 'Outbound';
  amount: number;
  createdAt?: Date;
  date?: string;
  /** Links are addressed by uid. Names come back read-only for display. */
  assetId?: string | null;
  assetName?: string | null;
  liabilityId?: string | null;
  liabilityName?: string | null;
  participantId?: string | null;
  participantName?: string | null;
  accountNo?: string;
  isRecurring?: boolean;
  recurrenceInterval?: RecurrenceUnit | null;
  /** Units between occurrences: quarterly is monthly with a count of 3. */
  recurrenceCount?: number;
  /** 1-12, or null when the schedule runs all year. Seasons may wrap the year end. */
  seasonStartMonth?: number | null;
  seasonEndMonth?: number | null;
  nextDueDate?: string | null;
}

export interface CashFlowsResponse {
  data: CashFlow[];
  total: number;
  page: number;
  limit: number;
}

// types.ts
export interface User {
  id: number;
  email: string;
  pendingEmail?: string | null;
  uid: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  sex: string | null;
  prefix: string | null;
  suffix: string | null;
  dob: string | null;
  representative: string | null;
  maritalStatus: string | null;
  deceased: boolean | null;
  specialCircumstance: string | null;
  terms: string | null;
  stage: string | null;
  profileComplete: boolean | null;
  /** Whether global search reaches past the active estate. */
  searchAllEstates?: boolean;
  userType: string | null;
  createdOn: string | null;
  updatedOn: string | null;
  updatedBy: string | null;
  lastLogin: string | null;
  lastLoginIp: string | null;
  lastLoginDevice: string | null;
  lastLoginLocation: string | null;
  lastLoginBrowser: string | null;
  otherLoginInfo: string | null;
  lastPasswordChange: string | null;
  passwordChangeCount: number | null;
  isAdmin: boolean;
  isSuperAdmin: boolean | null;
  isDeleted: boolean | null;
  isLocked: boolean | null;
  isVerified: boolean | null;
  activationCode: string | null;
  activationExpiry: string | null;
  profilePic: string | null;
  active: boolean | null;
  idBack: string | null;
  idFront: string | null;
  idType: string | null;
  idNumber: string | null;
  roles: string;
  ssn: string | null;
  /**
   * Taxpayer identification number, for a fiduciary that is an institution
   * rather than a person. Encrypted at rest and read back masked, so a value
   * still showing its mask must not be sent back as a number.
   */
  tin: string | null;
  dateOfDeath: string | null;
  phone: string | null;
  /** Country of citizenship, which the probate petition asks for by name. */
  citizenship?: string | null;
  /**
   * How far down the line of succession this person sits, where 1 takes first.
   * Null for anyone who inherits nothing, such as an executor or the estate's
   * lawyer. See BENEFICIARY_LEVELS.
   */
  beneficiaryLevel?: number | null;
}

export interface EstateOwner extends User {
  addresses?: UpdateAddress[];
}



// types.ts

export interface ChangeDetails {
  after?: Record<string, any>;
  before?: Record<string, any>;
  updatedColumns?: string[];
}

export interface AuditEvent {
  id: string;
  timestamp: string;
  entityName: string;
  changes: ChangeDetails;
  operation: string;
  severity: string;
  user?: User;
  accessLevel: string;
  tags: string[];
}


// types.ts

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  addressType: string;
  city: string;
  state: string;
  zipCode: string;
  postalCode?: string;
  county?: string;
  country?: string;
  primaryPhone?: string;
  alternatePhone1?: string;
  alternatePhone2?: string;
}
export interface UpdateAddress extends Address {
  uid?: string;
}
export interface AddParticipantPayload {
  
  email: string;
  firstName: string;
  middleName?: string | null;
  lastName: string | null;
  addresses: Address[];
  sex?: string | null;
  prefix?: string | null;
  suffix?: string | null;
  dob?: string | null;
  representative?: string | null;
  maritalStatus?: string | null;
  deceased?: boolean | null;
  specialCircumstance?: string | null;
  profileComplete?: boolean | null;
  userType?: string | null;
  relatedTo: string; // UID of the related user
  natureOfRelation: string;
  domicileAddress?: Address;
  mailingAddress?: Address;
  presentAddress?: Address;
  ssn?: string | null;
  tin?: string | null;
  dateOfDeath?: string; // ISO date string if deceased
  citizenship?: string | null;
  /** 1 takes first. Omitted for a participant who inherits nothing. */
  beneficiaryLevel?: number | null;
}


export interface ParticipantResponse extends Participant {
  uid: string;
  addresses: UpdateAddress[];
  estates: Estate[];
  relationships: any[];
  relationship: any[]
  /**
   * Where this person sits on the court's family tree affidavit, worked out
   * from the estate's relationships on every read rather than stored, so a
   * birth or a marriage renumbers nobody by hand. Read-only.
   *
   * Not to be confused with beneficiaryLevel, which is succession order set by
   * hand from the will: a grandchild standing in for a dead parent is a
   * generation further down the tree while taking at the same place in line.
   *
   * Hops from the decedent, who is 0. Their children and their brothers and
   * sisters are 1, grandchildren and nieces/nephews are 2. Null for anyone the
   * decedent cannot be reached from, such as a spouse who married in.
   */
  generation?: number | null;
  /** Whether the path runs down through children or sideways via a sibling. */
  line?: 'descendant' | 'collateral' | null;
  /** Hierarchical position: "1", "2.1", "2.3". */
  ordinal?: string | null;
  /** For somebody married in and no blood relation, the id they married. */
  spouseOf?: number | null;
}


export interface EditParticipantPayload extends Partial<AddParticipantPayload> {

  addresses?: UpdateAddress[];
}

export interface Participant extends User{
  // ... existing fields ...
  relatedTo: string;
  natureOfRelation: string;
  addresses: Address[];
   domicileAddress: Address;
   mailingAddress?: Address;
   presentAddress?: Address;
   // ... other fields ...
}

export interface Executor extends Participant {
  // Additional fields if any
}


export interface Estate {
  id: number;
  uuid: string;
  estateName: string;
  description: string;
  /** Two-letter code for the state whose law governs the estate. */
  state?: string | null;
  /** County the petition is filed in. */
  county?: string | null;
  /**
   * The estate's own address, which is not the decedent's: the estate is a
   * legal person in its own right, and its post goes wherever it is
   * administered from.
   */
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  zipCode?: string | null;
  /** The estate's own IRS tax id, formatted NN-NNNNNNN. */
  ein?: string | null;
  createdAt: Date;
  updatedAt: string;
  participants: Participant[];
  executor: User[];
  /** The lawyers acting for the estate, a roster of their own. */
  lawyers: User[];
  users: User[];
  guests: User[];
  invites: User[];
  address?: string;
  estateOwner: EstateOwner;
  assets: any[]; // TODO: define more accurately based on API
}

export interface EstateResponse {
  particpants: ParticipantResponse[];
  
}
/**
 * Inviting somebody to act for an estate.
 *
 * The detail past name and email is what the probate petition asks of whoever
 * signs it. This invite is the only form an executor or a lawyer ever sees, so
 * their profile has nowhere else to come from.
 */
/**
 * What an invite can say about the person being invited.
 *
 * Everything past the email is optional in both directions. An invite can go
 * out on an address alone, which is what keeps sending one easy, and where the
 * inviter already has the details to hand they can fill them in so the
 * invitee arrives at a profile that is mostly written. None of it goes into
 * the email: one person typed it about another, and until the invitee
 * confirms it none of it is verified.
 */
export interface EstateRoleInvitePayload {
  name: string;
  email: string;
  /** Title, e.g. Mrs. */
  prefix?: string;
  /**
   * Name parts, where the inviter knows them separately. `name` still works
   * on its own and is split when these are absent, but a name is not reliably
   * two words: "Carruthers Peker" is one family name.
   */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** Suffix, e.g. Esq, Jr. */
  suffix?: string;
  phone?: string;
  /** YYYY-MM-DD. */
  dob?: string;
  /** Stored encrypted, as everywhere else an SSN is captured. */
  ssn?: string;
  /**
   * Taxpayer identification number, for a fiduciary that is an institution.
   * An executor need not be a person: a bank's trust department or a trust
   * company has a TIN where a person has an SSN.
   */
  tin?: string;
  citizenship?: string;
  /** County of their domicile address. */
  county?: string;
  /** Their domicile address, as far as the inviter knows it. */
  address?: EstateRoleInviteAddress;
}

/** Every line optional: the inviter may know the town but not the street. */
export interface EstateRoleInviteAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
}

/**
 * The details a court form asks of an executor or an attorney. Named by the
 * accept-invite response so the page can ask for exactly what is still blank.
 */
export type MissingProfileField =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'dob'
  | 'taxId'
  | 'addressLine1'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'county';

/**
 * What an invite link hands back, whether it was just accepted or the invitee
 * has since filled in their details: who this is, and what is left to do
 * before the estate's paperwork can name them.
 */
export interface AcceptInviteResponse {
  message: string;
  user: { email: string; firstName?: string | null; lastName?: string | null };
  /** True while the account still has whatever password the invite created. */
  needsPassword: boolean;
  missingProfileFields: MissingProfileField[];
  /** Present on acceptance, so the page can offer to fill in what is missing. */
  inviteToken?: string;
  /** Present on acceptance. Short-lived, and the only way to set a password. */
  passwordResetLink?: string;
}

export type EstateRole = 'executor' | 'lawyer';

export interface EstateRoleInviteResponse {
  message: string;
}


export interface ApiErrorResponse {
  statusCode: number;
  message: string;
}

/**
  * Editing an estate edits the estate.
  */
export interface EditEstatePayload {
  description: string;
  estateName: string;
  state?: string;
  county?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  /** Nine digits, formatted NN-NNNNNNN. Empty clears it. */
  ein?: string;
}
export interface EditEstateResponse extends Estate {}

export interface CreateEstatePayload {
  dob: string; // "2022-09-27 18:00:00.000"
  description: string;
  estateName: string;
  firstName: string;
  isDeceased: boolean;
  lastName: string;
  middleName: string;
  prefix: string;
  sex: string;
  suffix: string;
  state?: string;
  county?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  ein?: string;
  phone?: string;
  maritalStatus?: string;
  citizenship?: string;
  addresses?: UpdateAddress[];
}
export interface CreateEstateResponse extends Estate {}

/** The estate owner, edited on its own. */
export interface EditEstateOwnerPayload {
  email?: string;
  prefix?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  sex?: string;
  dob?: string;
  phone?: string;
  maritalStatus?: string;
  citizenship?: string;
  isDeceased?: boolean;
  dateOfDeath?: string;
  placeOfDeathName?: string;
  placeOfDeathAddress?: string;
  ssn?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  addresses?: UpdateAddress[];
}

export interface AssetsResponse {
  data: Asset[];
  total: number;
  totalValue: number;
  totalValueLastMonth: number;
  averageAssetValue: number;
  // categoryDistribution: { [category: string]: number };
  page: number;
  lastPage: number;
}

export interface  Portfolio {
  ticker?: string;
  internalCashDistributionAmount?: number;
  equity?: string;
  originalQuantityOwned?: number;
  priorDayClosingPrice?: number;
  dateOfPurchase?: string; 
  currentQuantityOwned?: string;
  costBasis?: number;
}
export interface Asset {
  id: number;
  uid:string;
  /** Short reference shown to users, unique within the estate: A-014. */
  code: string;
  estateIdentifier: string;
  name: string;
  coveredInWill: boolean;
  ref: string;
  itemNumber: string;
  EPTLExemption: boolean;
  isSmallEstate: boolean;
  location: string;
  category: string;
  description: string;
  secondaryIdentifier: string;
  marketValue: number;
  lease: boolean;
  /** What is owed against the asset. */
  liens: number;
  /** Most recent bank or broker statement figure. Provenance only, not valued. */
  statementValue?: number;
  dateOfDeathValue?: number;
  distributionValue?: number;
  baselineDate?: string;
  distributionDate?: string;
  baselineSource?: string;
  changeSinceDeath?: number;
  changeSinceDeathPercent?: number;
  changeOnDistribution?: number;
  changeOnDistributionPercent?: number;

  propertyClass?: string;
  jurisdictionState?: string;
  firearmMake?: string;
  firearmModel?: string;
  firearmCaliber?: string;
  firearmSerialNumber?: string;

  netOfLien: number;
  valueType: string;
  valueSource: string;
  valuationBasis?: ValuationBasis;
  originalMarketValue?: number;
  eptlCategory?: EptlCategory | null;
  status: AssetStatus;
  discountToMarket: number;
  estimatedValue: number;
  realizedValue: number;
  familyDiscount: number;
  internalCashlessDistribution: boolean;
  internalCashlessDistributionAmount: number;
  internalCashDistribution: boolean;
  estateValueIfInWill: number;
  estateValueIfJointlyOwned: number;
  jointlyOwned: boolean;
  sold: boolean;
  donate: boolean;
  givenToFriend: boolean;
  thrownOut: boolean;
  cashReceived: number;
  percentageAwayFromEstValue: number;
  percentJointlyOwned: number;
  portfolioId: number;
  liabilityBalance: number;
  createdAt: Date;
  updatedAt: Date;
  costBasis: number;
  currentQuantityOwned: number;
  realisedExternalSale?: number;
  internalDistributionAmount?: number;
  estateValue?: number;
  /** An amount, not a flag: the value counted toward probate administration. */
  toBeCountedInProbate?: number;
  countedInProbateValue?: number;
  familyDiscountedValue?: number;
  jointlyOwnedValue?: number;
  smallEstateValue?: number;
  estateLeftToBeDistributed?: number;
  /** An amount handed out, not a flag. Any value means the asset has gone out. */
  distributed?: number;
  /** Shares of the estate, as fractions of 1. */
  percantageOfEstateAtDiscount?: number;
  shareOfEstateDiscount?: number;
  shareOfTotalEstateDiscount?: number;
  EPTLExemptionAmount?: number;
  residualEPTLExemptionAmount?: number;

  portfolio?: Portfolio;

  ticker?: string;
  internalCashDistributionAmount?: number;
  equity?: string;
  originalQuantityOwned?: number;
  priorDayClosingPrice?: number;
  dateOfPurchase?: string;

}
export interface AssetsResponse {
  data: Asset[];
  total: number;
  totalValue: number;
  totalValueLastMonth: number; // For comparison
  page: number;
  lastPage: number;
}

// PDF Field Mapping Types
export interface PdfFieldMapping {
  id: number;
  uid: string;
  pdfFieldName: string;           // Original field name from PDF
  databaseFieldName: string | null; // Mapped database field (e.g., "user.firstName")
  relatedEntity: string | null;   // Entity type (e.g., "user", "estate", "asset")
  /**
   * Which row to use when the entity holds several: "[1]" by position, or
   * "[field=value]" by a match. Null means the first row.
   */
  entitySelector?: string | null;
  pageNumber: number;
  type: string;                   // "TextField", "CheckBox", etc.
  surroundingText: string;        // Context text near the field
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  defaultValue: string | null;
  template?: string | null;      // Template expression (supersedes databaseFieldName), e.g. "{{user.firstName}} {{user.lastName}}"
  probateDocId?: string;
}

export interface MappableEntityField {
  value: string;
  label: string;
}

export interface MappableEntity {
  label: string;
  fields: MappableEntityField[];
  /**
   * True when an estate holds several of these, so a mapping has to say which
   * one it means. Estates, the owner and the decedent are singular; assets,
   * participants and addresses are not.
   */
  multiple?: boolean;
  /**
   * Fields worth offering as a way to pick a row, beyond position. Used for
   * people, where "the surviving spouse" is stabler than "the second one".
   */
  selectBy?: MappableEntityField[];
  /**
   * Kept so saved mappings still read back and existing documents keep
   * filling, but no longer offered when choosing an entity.
   */
  hidden?: boolean;
}

/**
 * The entities offered in the entity picker, in catalogue order.
 *
 * `current` is the entity already saved on the mapping being edited. Passing it
 * keeps a hidden entity visible for as long as that mapping still uses it, so
 * an older mapping shows what it points at instead of an empty box.
 */
export const mappableEntityOptions = (
  current?: string,
): Array<[string, MappableEntity]> =>
  Object.entries(MAPPABLE_ENTITIES).filter(
    ([key, entity]) => !entity.hidden || key === current,
  );

/** The headings the entity picker is divided into, in the order they appear. */
export const ENTITY_GROUP_ORDER = [
  'The Estate',
  'People on the Estate',
  'Family Tree: Descendants',
  'Family Tree: Ancestors',
  'Assets and Liabilities',
  'Dates',
] as const;

export type MappableEntityGroup = (typeof ENTITY_GROUP_ORDER)[number];

/**
 * Which heading each entity sits under.
 *
 * The family tree alone contributes two dozen entities, and listing them flat
 * beside Estate and Executor buried everything else: the picker became a wall
 * of names where "First Cousin Once Removed, Maternal Side" sat directly under
 * "Estate Owner". The two family tree headings follow the form itself, which
 * is one chart of descendants on the front and a different chart of ancestors
 * on the back.
 *
 * Kept as a lookup rather than a field on each entity so the catalogue above
 * stays readable. An entity missing from here is a mistake rather than a
 * default, and mappableFields.test.ts says so.
 */
const ENTITY_GROUPS: Record<string, MappableEntityGroup> = {
  estate: 'The Estate',
  owner: 'The Estate',
  user: 'The Estate',
  address: 'The Estate',
  totals: 'The Estate',

  participant: 'People on the Estate',
  executor: 'People on the Estate',
  lawyer: 'People on the Estate',
  benefactor: 'People on the Estate',

  decedentSpouse: 'Family Tree: Descendants',
  children: 'Family Tree: Descendants',
  grandchildren: 'Family Tree: Descendants',
  greatGrandchildren: 'Family Tree: Descendants',
  siblings: 'Family Tree: Descendants',
  niecesNephews: 'Family Tree: Descendants',
  grandNiecesNephews: 'Family Tree: Descendants',

  father: 'Family Tree: Ancestors',
  mother: 'Family Tree: Ancestors',
  paternalGrandfather: 'Family Tree: Ancestors',
  paternalGrandmother: 'Family Tree: Ancestors',
  maternalGrandfather: 'Family Tree: Ancestors',
  maternalGrandmother: 'Family Tree: Ancestors',
  grandparents: 'Family Tree: Ancestors',
  auntsUncles: 'Family Tree: Ancestors',
  firstCousins: 'Family Tree: Ancestors',
  firstCousinsOnceRemoved: 'Family Tree: Ancestors',
  paternalAuntsUncles: 'Family Tree: Ancestors',
  maternalAuntsUncles: 'Family Tree: Ancestors',
  paternalFirstCousins: 'Family Tree: Ancestors',
  maternalFirstCousins: 'Family Tree: Ancestors',
  paternalFirstCousinsOnceRemoved: 'Family Tree: Ancestors',
  maternalFirstCousinsOnceRemoved: 'Family Tree: Ancestors',

  claim: 'Assets and Liabilities',
  firearm: 'Assets and Liabilities',
  asset: 'Assets and Liabilities',
  liability: 'Assets and Liabilities',

  date: 'Dates',
  computed: 'Dates',
};

/** The heading an entity belongs under, for the picker and its test. */
export const entityGroupOf = (key: string): MappableEntityGroup | undefined =>
  ENTITY_GROUPS[key];

/**
 * The picker's contents, divided into headings and with empty headings
 * dropped, so a group whose only member is a retired entity does not draw as a
 * bare label.
 */
export const groupedMappableEntities = (
  current?: string,
): Array<[MappableEntityGroup, Array<[string, MappableEntity]>]> => {
  const offered = mappableEntityOptions(current);

  return ENTITY_GROUP_ORDER.map(
    (group) =>
      [group, offered.filter(([key]) => ENTITY_GROUPS[key] === group)] as [
        MappableEntityGroup,
        Array<[string, MappableEntity]>,
      ],
  ).filter(([, members]) => members.length > 0);
};

/**
 * The fields a person on the family tree affidavit is asked for, shared by
 * every column of it: a grandchild is asked the same questions as a child.
 *
 * `generation` and `ordinal` are worked out from the estate's relationships
 * when a document is filled rather than stored on anybody, so they cannot
 * drift out of step with the tree the way a hand-kept numbering does.
 *
 * Mirrors KIN_FIELDS in wzee-be's src/probatedocs/mappable-fields.ts.
 */
const KIN_FIELDS: MappableEntityField[] = [
  { value: 'prefix', label: 'Prefix' },
  { value: 'firstName', label: 'First Name' },
  { value: 'middleName', label: 'Middle Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'suffix', label: 'Suffix' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'dob', label: 'Date of Birth' },
  { value: 'ssn', label: 'Social Security Number' },
  { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
  { value: 'citizenship', label: 'Citizenship' },
  { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
  { value: 'generation', label: 'Generation (hops from the decedent)' },
  { value: 'ordinal', label: 'Position on the tree (1, 2.1, 2.3)' },
  { value: 'underDisability', label: 'Under Disability' },
  { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
  { value: 'addresses[0].addressLine2', label: 'Address Line 2' },
  { value: 'addresses[0].city', label: 'City' },
  { value: 'addresses[0].state', label: 'State' },
  { value: 'addresses[0].county', label: 'County' },
  { value: 'addresses[0].zipCode', label: 'Zip Code' },
  { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
  { value: 'fullAddress', label: 'Full Address (one line)' },
  // Somebody who married in holds no column of their own on the form, so their
  // details are reached through the blood relative they married.
  { value: 'spouse.firstName', label: 'Spouse: First Name' },
  { value: 'spouse.lastName', label: 'Spouse: Last Name' },
  { value: 'spouse.dob', label: 'Spouse: Date of Birth' },
  { value: 'spouse.fullName', label: 'Spouse: Full Name' },
  { value: 'spouse.fullAddress', label: 'Spouse: Full Address (one line)' },
  // Whose child they are. The great grandchildren column is a free list rather
  // than slots beside each grandchild, so a box names the parent in words.
  { value: 'parent.firstName', label: 'Parent: First Name' },
  { value: 'parent.lastName', label: 'Parent: Last Name' },
  { value: 'parent.fullName', label: 'Parent: Full Name' },
];

/** Ways to name a row of a family tree column other than by position. */
const KIN_SELECT_BY: MappableEntityField[] = [
  { value: 'ordinal', label: 'Position on the tree (1, 2.1, 2.3)' },
  { value: 'lastName', label: 'Last Name' },
];

export const MAPPABLE_ENTITIES: Record<string, MappableEntity> = {
  owner: {
    label: 'Estate Owner (the legal person whose estate this is)',
    fields: [
      { value: 'prefix', label: 'Prefix' },
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'suffix', label: 'Suffix' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'sex', label: 'Sex' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'maritalStatus', label: 'Marital Status' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
      { value: 'citizenship', label: 'Citizenship' },
      { value: 'dateOfDeath', label: 'Date of Death' },
      { value: 'placeOfDeathName', label: 'Place of Death' },
      { value: 'placeOfDeathAddress', label: 'Place of Death Address' },
      { value: 'willExists', label: 'Will Exists' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].addressLine2', label: 'Address Line 2' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].county', label: 'County' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'addresses[0].country', label: 'Country' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  participant: {
    label: 'Beneficiary / Participant (inherits, e.g. a child)',
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'beneficiaryLevel', label: 'Beneficiary Level (1 takes first)' },
    ],
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
      { value: 'citizenship', label: 'Citizenship' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'beneficiaryLevel', label: 'Beneficiary Level (number, 1 takes first)' },
      { value: 'beneficiaryLevelLabel', label: 'Beneficiary Level (in words)' },
      { value: 'generation', label: 'Generation (hops from the decedent)' },
      { value: 'ordinal', label: 'Position on the tree (1, 2.1, 2.3)' },
      { value: 'underDisability', label: 'Under Disability' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].county', label: 'County' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  executor: {
    label: 'Executor',
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'lastName', label: 'Last Name' },
    ],
    fields: [
      { value: 'prefix', label: 'Prefix' },
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'suffix', label: 'Suffix' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
      { value: 'citizenship', label: 'Citizenship' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].addressLine2', label: 'Address Line 2' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].county', label: 'County' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  /**
   * The estate's own attorney, who inherits nothing but whom the court forms
   * name in their own right, the same way they name the executor.
   */
  lawyer: {
    label: 'Lawyer for the Estate',
    multiple: true,
    selectBy: [
      { value: 'lastName', label: 'Last Name' },
    ],
    fields: [
      { value: 'prefix', label: 'Prefix' },
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'suffix', label: 'Suffix' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
      { value: 'citizenship', label: 'Citizenship' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].addressLine2', label: 'Address Line 2' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].county', label: 'County' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  liability: {
    label: 'Liability',
    multiple: true,
    selectBy: [
      { value: 'name', label: 'Name' },
    ],
    fields: [
      {value:'unsecuredAmount', label:'Unsecured claim amount'},
      { value: 'creditorUid', label: 'Creditor uid' },
      { value: 'collateralDescription', label: 'Collateral description' },
      { value: 'lienNature', label: 'Lien nature' },
      { value: 'accountReference', label: 'Account reference' },
      { value: 'debtor', label: 'debtor' },
      { value: 'deathTreatment', label: 'Death treatment' },
      { value: 'claimCategory', label: 'Claim category' },
      { value: 'status', label: 'status' },
      { value: 'dateIncurred', label: 'Date incurred' },
      { value: 'closedDate', label: 'Closed date' },
      { value: 'dateOfDeathBalance', label: 'Date of death balance' },
      { value: 'outstandingBalance', label: 'Outstanding balance' },
      { value: 'securedAmount', label: 'Secured amount' },
      { value: 'collateralValue', label: 'Collateral value' },

      { value: 'name', label: 'Name' },
      { value: 'description', label: 'Description' },
      { value: 'amount', label: 'Amount' },
      { value: 'dueDate', label: 'Due Date' },
    ]
  },
  // Renamed to `date`. Still resolved, so mappings written against the old
  // name keep filling on a database the migration has not reached.
  computed: {
    label: 'Computed',
    hidden: true,
    fields: [
      { value: 'today', label: "Today's Date" },
    ]
  },
  user: {
    label: 'User (whoever is signed in)',
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'ssn', label: 'Social Security Number' },
      { value: 'tin', label: 'Taxpayer ID (TIN, for an executor that is an institution)' },
      // Singular, because that is what these mappings already say. The column
      // is `addresses`, and the resolver reads the first of them.
      { value: 'address.addressLine1', label: 'Address Line 1' },
      { value: 'address.city', label: 'City' },
      { value: 'address.state', label: 'State' },
      { value: 'address.zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  },
  estate: {
    label: 'Estate',
    fields: [
      { value: 'estateName', label: 'Estate Name' },
      { value: 'description', label: 'Description' },
      { value: 'ein', label: 'EIN (estate tax id)' },
      // The estate's own address, not the decedent's. Theirs is under Decedent.
      { value: 'addressLine1', label: 'Estate Address Line 1' },
      { value: 'addressLine2', label: 'Estate Address Line 2' },
      { value: 'city', label: 'Estate City' },
      { value: 'state', label: 'Estate State' },
      { value: 'county', label: 'Estate County' },
      { value: 'zipCode', label: 'Estate Zip Code' },
      { value: 'fullAddress', label: "Estate's Full Address (one line)" },
      { value: 'createdAt', label: 'Created Date' },
    ]
  },
  /**
   * The address of whoever is signed in, which is almost never what a court
   * form is asking for.
   *
   * It reads the downloading user's own addresses, so the value printed
   * changes with who clicks download: a paralegal generating the petition got
   * their own county where the estate's or the decedent's belonged. Every
   * entity that has an address now offers its own (the estate's own lines, and
   * `addresses[0].*` on each person), so this is no longer offered.
   *
   * Still resolved, because mappings already point at it, and labelled for
   * what it is so those can be seen and moved.
   */
  address: {
    label: 'Address (of whoever is signed in, not the estate)',
    hidden: true,
    multiple: true,
    fields: [
      { value: 'addressLine1', label: 'Address Line 1' },
      { value: 'addressLine2', label: 'Address Line 2' },
      { value: 'city', label: 'City' },
      { value: 'state', label: 'State' },
      { value: 'zipCode', label: 'Zip Code' },
      { value: 'county', label: 'County' },
      { value: 'country', label: 'Country' },
    ]
  },
  claim: { label: 'Open claims (no-debt affidavit)', multiple: true, selectBy: [{value: 'name', label: 'Creditor name'}], fields: [{'value': 'name', 'label': 'Name'}, {'value': 'address', 'label': 'Address'}, {'value': 'amount', 'label': 'Amount'}, {'value': 'description', 'label': 'Description'}, {'value': 'category', 'label': 'Category'}] },
  firearm: { label: 'Firearms inventory', multiple: true, selectBy: [{value: 'firearmSerialNumber', label: 'Serial number'}, {value: 'name', label: 'Asset name'}], fields: [
    { value: 'name', label: 'name' },
    { value: 'firearmMake', label: 'Firearm make' },
    { value: 'firearmModel', label: 'Firearm model' },
    { value: 'firearmCaliber', label: 'Firearm caliber' },
    { value: 'firearmSerialNumber', label: 'Firearm serial number' },
    { value: 'marketValue', label: 'Market value' },
  ] },
  asset: {
    label: 'Asset',
    multiple: true,
    selectBy: [
      { value: 'name', label: 'Asset Name' },
      { value: 'category', label: 'Asset Type' },
      { value: 'ref', label: 'Reference' },
    ],
    fields: [
      { value: 'name', label: 'Asset Name' },
      { value: 'description', label: 'Description' },
      { value: 'marketValue', label: 'Market Value' },
      { value: 'estimatedValue', label: 'Estimated Value' },
      { value: 'realisedExternalSale', label: 'Realized External Sale' },
      { value: 'residualEPTLExemptionAmount', label: 'Residual EPTL Exemption' },
      { value: 'estateValue', label: 'Estate Value' },
      { value: 'jointlyOwnedValue', label: 'Jointly Owned Value' },
      { value: 'smallEstateValue', label: 'Small Estate Value' },
      { value: 'countedInProbateValue', label: 'Counted in Probate Value' },
      { value: 'cashReceived', label: 'Cash Received' },
      { value: 'internalDistributionAmount', label: 'Internal Distribution Amount' },
      { value: 'internalCashDistributionAmount', label: 'Internal Cash Distribution Amount' },
      { value: 'estateLeftToBeDistributed', label: 'Estate Left To Be Distributed' },
      { value: 'EPTLExemptionAmount', label: 'EPTL Exemption Amount' },
      { value: 'familyDiscountedValue', label: 'Family Discounted Value' },
      { value: 'shareOfEstateDiscount', label: 'Share of Estate Discount' },
      { value: 'shareOfTotalEstateDiscount', label: 'Share of Total Estate Discount' },
      { value: 'percentageAwayFromEstValue', label: 'Percentage Away From Est Value' },
      { value: 'liens', label: 'Lien Against Asset' },
      { value: 'statementValue', label: 'Recent Statement Value' },
      { value: 'dateOfDeathValue', label: 'Date of death value' },
      { value: 'distributionValue', label: 'Distribution value' },
      { value: 'baselineDate', label: 'Baseline date' },
      { value: 'distributionDate', label: 'Distribution date' },
      { value: 'baselineSource', label: 'Baseline source' },
      { value: 'changeSinceDeath', label: 'Change since death' },
      { value: 'changeSinceDeathPercent', label: 'Change since death percent' },
      { value: 'changeOnDistribution', label: 'Change on distribution' },
      { value: 'changeOnDistributionPercent', label: 'Change on distribution percent' },

      { value: 'propertyClass', label: 'Property class' },
      { value: 'jurisdictionState', label: 'Jurisdiction state' },
      { value: 'firearmMake', label: 'Firearm make' },
      { value: 'firearmModel', label: 'Firearm model' },
      { value: 'firearmCaliber', label: 'Firearm caliber' },
      { value: 'firearmSerialNumber', label: 'Firearm serial number' },

      { value: 'netOfLien', label: 'Net of Lien' },
      { value: 'location', label: 'Location' },
      { value: 'category', label: 'Category' },
    ]
  },
  totals: {
    label: 'Estate Totals',
    fields: [
      {value:'reportGrossEstate', label:'reportGrossEstate'},
      {value:'reportDistributedToBeneficiaries', label:'reportDistributedToBeneficiaries'},
      {value:'reportRemainingInHands', label:'reportRemainingInHands'},
      {value:'reportMissingBaselineCount', label:'reportMissingBaselineCount'},
      {value:'reportReconciliationDifference', label:'reportReconciliationDifference'},

      { value: 'firearmInventoryTotal', label: 'Firearm inventory total' },
      { value: 'petitionPersonalProperty', label: 'Petition personal property' },
      { value: 'petitionImprovedRealPropertyNY', label: 'Petition improved real property ny' },
      { value: 'petitionUnimprovedRealPropertyNY', label: 'Petition unimproved real property ny' },
      { value: 'petitionLowerValue', label: 'Petition lower value' },
      { value: 'petitionUpperValue', label: 'Petition upper value' },
      { value: 'unclassifiedAssetCount', label: 'Unclassified asset count' },
      { value: 'unknownJurisdictionCount', label: 'Unknown jurisdiction count' },
      { value: 'beneficiariesDistributedValue', label: 'Beneficiaries distributed value' },
      { value: 'missingDistributionValueCount', label: 'Missing distribution value count' },
      { value: 'netRemainingAssetValue', label: 'Net remaining asset value' },
      { value: 'projectedGrossRents18Months', label: 'Projected gross rents18months' },
      { value: 'rentalProjectionFrom', label: 'Rental projection from' },
      { value: 'rentalProjectionTo', label: 'Rental projection to' },
      { value: 'noDebtPersonalPropertyAndRents', label: 'No debt personal property and rents' },
      { value: 'noDebtClaimsNone', label: 'Confirmed absence of claims' },
      { value: 'noDebtClaims', label: 'No debt claims' },
      { value: 'noDebtConfirmationName', label: 'No debt confirmation name' },
      { value: 'noDebtConfirmationDate', label: 'No debt confirmation date' },
      { value: 'noDebtThreshold', label: 'No debt threshold' },

      { value: 'estateValue', label: 'Total Estate Value (gross)' },
      // The gross counts every asset, including those already handed out. What
      // is still in the estate, and what has left it, are reported separately.
      { value: 'remainingEstateValue', label: 'Remaining Estate Value (not yet disposed)' },
      { value: 'distributedEstateValue', label: 'Distributed Estate Value (disposed)' },
      // What the court is actually asked for on a petition: the gross estate
      // less the property set aside by statute.
      { value: 'probateEstateValue', label: 'Probate Estate Value (after exempt property)' },
      { value: 'exemption.totalExempt', label: 'Total Exempt Property' },
      { value: 'exemption.totalExcess', label: 'Exempt Property Over the Cap' },
      { value: 'exemption.statute', label: 'Exempt Property Statute (e.g. EPTL 5-3.1)' },
      { value: 'totalAssets', label: 'Total Assets Count' },
      { value: 'totalLiabilities', label: 'Total Liabilities Count' },
      { value: 'totalInboundCashflow', label: 'Total Inbound Cashflow' },
      { value: 'totalOutboundCashflow', label: 'Total Outbound Cashflow' },
      { value: 'marketValueSum', label: 'Total Market Value' },
      { value: 'estimatedValueSum', label: 'Total Estimated Value' },
      { value: 'realizedExternalSaleSum', label: 'Total Realized External Sale' },
      { value: 'EPTLExemptionAmountSum', label: 'Total EPTL Exemption Amount' },
      { value: 'residualEPTLExemptionAmountSum', label: 'Total Residual EPTL Exemption' },
      { value: 'estateValueSum', label: 'Total Estate Value Sum' },
      { value: 'jointlyOwnedValueSum', label: 'Total Jointly Owned Value' },
      { value: 'smallEstateValueSum', label: 'Total Small Estate Value' },
      { value: 'countedInProbateValueSum', label: 'Total Counted in Probate Value' },
      { value: 'cashReceivedSum', label: 'Total Cash Received' },
      { value: 'internalDistributionAmountSum', label: 'Total Internal Distribution Amount' },
      { value: 'internalCashLessDistributionAmountSum', label: 'Total Internal Cashless Distribution' },
      { value: 'estateLeftToBeDistributed', label: 'Total Estate Left To Be Distributed' },
      { value: 'liensSum', label: 'Total Liens' },
      { value: 'netLiensSum', label: 'Total Net Liens' },
      { value: 'shareOfEstateDiscountSum', label: 'Total Share of Estate Discount' },
      { value: 'shareOfTotalEstateDiscountSum', label: 'Total Share of Total Estate Discount' },
      { value: 'totalLiabilityValue', label: 'Total Liability Value' },
      { value: 'totalCashflowValue', label: 'Total Cashflow Value' },
    ]
  },
  // Dates a form asks for that nobody types in. Every other date on a court
  // form belongs to a person or an asset, so it is mapped from there: a date of
  // death from the Estate Owner, a due date from a Liability.
  date: {
    label: 'Date',
    fields: [
      { value: 'today', label: "Today's Date (when the document is generated)" },
    ]
  },
  /**
   * One entity per column of the court's family tree affidavit, each already
   * in the order that form's rows run: down each parent's children in turn,
   * oldest first. "2nd Child" is a question the estate's participant list
   * cannot answer, because it holds the spouses and the executor as well.
   *
   * The form offers two readings of the same three columns and the preparer
   * crosses out the class that does not apply, so both are offered: an estate
   * with descendants fills Children / Grandchildren / Great Grandchildren, and
   * one without fills Brothers/Sisters / Nieces/Nephews / Grandnieces and
   * Grandnephews.
   */
  children: {
    label: 'Child of the Decedent (1st generation, oldest first)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  grandchildren: {
    label: 'Grandchild of the Decedent (2nd generation)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  greatGrandchildren: {
    label: 'Great Grandchild of the Decedent (3rd generation)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  siblings: {
    label: 'Brother or Sister of the Decedent (where there are no children)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  niecesNephews: {
    label: 'Niece or Nephew of the Decedent (where there are no children)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  grandNiecesNephews: {
    label: 'Grandniece or Grandnephew (where there are no children)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  decedentSpouse: {
    label: "Decedent's Spouse",
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  /**
   * The reverse side of the family tree form, walked up from the decedent
   * rather than down. Completed where the decedent left no descendants, to
   * establish the nearest distributees, so these are normally all blank.
   */
  father: { label: 'Father of the Decedent', fields: KIN_FIELDS },
  mother: { label: 'Mother of the Decedent', fields: KIN_FIELDS },
  paternalGrandfather: { label: 'Paternal Grandfather', fields: KIN_FIELDS },
  paternalGrandmother: { label: 'Paternal Grandmother', fields: KIN_FIELDS },
  maternalGrandfather: { label: 'Maternal Grandfather', fields: KIN_FIELDS },
  maternalGrandmother: { label: 'Maternal Grandmother', fields: KIN_FIELDS },
  grandparents: {
    label: 'Grandparent of the Decedent (all four, senior first)',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  auntsUncles: {
    label: 'Aunt or Uncle of the Decedent',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  firstCousins: {
    label: 'First Cousin of the Decedent',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  firstCousinsOnceRemoved: {
    label: 'First Cousin Once Removed',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  // Each column of the reverse chart is two blocks, one per grandparent
  // couple, and a box in the upper block must never fill from the lower one.
  paternalAuntsUncles: {
    label: 'Aunt or Uncle, Paternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  maternalAuntsUncles: {
    label: 'Aunt or Uncle, Maternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  paternalFirstCousins: {
    label: 'First Cousin, Paternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  maternalFirstCousins: {
    label: 'First Cousin, Maternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  paternalFirstCousinsOnceRemoved: {
    label: 'First Cousin Once Removed, Paternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  maternalFirstCousinsOnceRemoved: {
    label: 'First Cousin Once Removed, Maternal Side',
    multiple: true,
    selectBy: KIN_SELECT_BY,
    fields: KIN_FIELDS,
  },
  // The Estate Owner is the person these forms name, and offering Benefactor
  // beside it only asks which of the two a field means. Still resolved, so
  // mappings already written against it keep filling.
  benefactor: {
    label: 'Benefactor (owns the estate, e.g. a parent)',
    hidden: true,
    multiple: true,
    selectBy: [
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
    ],
    fields: [
      { value: 'firstName', label: 'First Name' },
      { value: 'middleName', label: 'Middle Name' },
      { value: 'lastName', label: 'Last Name' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'dob', label: 'Date of Birth' },
      { value: 'relationshipToDecedent', label: 'Relationship to Decedent' },
      { value: 'underDisability', label: 'Under Disability' },
      { value: 'addresses[0].addressLine1', label: 'Address Line 1' },
      { value: 'addresses[0].city', label: 'City' },
      { value: 'addresses[0].state', label: 'State' },
      { value: 'addresses[0].zipCode', label: 'Zip Code' },
      { value: 'fullName', label: 'Full Name (Prefix First Middle Last Suffix)' },
      { value: 'fullAddress', label: 'Full Address (one line)' },
    ]
  }
};